<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module v-model="form.stdInfo" :board="this.board" :is-print="isPrint" />

    <!-- 조사 구분 -->
    <div>
      <h5><i class="dot"></i> 조사 구분</h5>
      <div class="grid grid-cols-4 gap-x-6 gap-y-2 py-4">
        <fragment v-if="isPrint">
          <PrintCheckbox
            v-for="(value, v_i) in ['정기조사', '수시조사 - 근골격계 질환자 발생 시', '수시조사 - 새로운 작업 및 설비 도입 시', '수시조사 - 업무의 양과 작업공정 등 작업환경 변경 시']"
            :key="`구분_print_${value}`"
            :label="value"
            :value="form.regularly === value"
            color
            :style="v_i === 2 ? { gridColumn: '2 / 2', gridRow: '2 / 2' } : v_i === 3 ? { gridColumn: '2 / 2', gridRow: '3 / 3' } : {}"
          />
        </fragment>
        <fragment v-else>
          <vs-checkbox
            v-for="(value, v_i) in ['정기조사', '수시조사 - 근골격계 질환자 발생 시', '수시조사 - 새로운 작업 및 설비 도입 시', '수시조사 - 업무의 양과 작업공정 등 작업환경 변경 시']"
            :key="value"
            :style="v_i === 2 ? { gridColumn: '2 / 2', gridRow: '2 / 2' } : v_i === 3 ? { gridColumn: '2 / 2', gridRow: '3 / 3' } : {}"
            :checked="form.regularly === value"
            @change="form.regularly = form.regularly === value ? '' : value"
          >
            {{ value }}
          </vs-checkbox>
        </fragment>
      </div>
    </div>

    <!-- 조사 개요 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 조사 개요</h5>
      <div class="writeGridContainer">
        <div>
          <div>조사 일시</div>
          <span v-if="isPrint" class="print-input"> {{ form.조사일시 }} </span>
          <DateSelector v-else :readonly="board.readonly" v-model="form.조사일시" />
        </div>
        <div>
          <div>조사자</div>
          <span v-if="isPrint" class="print-input"> {{ form.조사자 }} </span>
          <ml-input v-else :readonly="board.readonly" v-model="form.조사자" />
        </div>
      </div>
      <div class="writeGridContainer">
        <div>
          <div>부서명</div>
          <span v-if="isPrint" class="print-input"> {{ form.부서명 }} </span>
          <ml-input v-else :readonly="board.readonly" v-model="form.부서명" />
        </div>
        <div>
          <div>작업공정명</div>
          <span v-if="isPrint" class="print-input"> {{ form.작업공정명 }} </span>
          <ml-input v-else :readonly="board.readonly" v-model="form.작업공정명" />
        </div>
        <div>
          <div>작업명</div>
          <span v-if="isPrint" class="print-input"> {{ form.작업명 }} </span>
          <ml-input v-else :readonly="board.readonly" v-model="form.작업명" />
        </div>
      </div>
    </div>

    <!-- 작업장 상황 조사 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 작업장 상황 조사</h5>
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div>작업 설비</div>
          <radio-text-input
            :readonly="board.readonly"
            :isPrint="isPrint"
            radioName="작업장상황조사_1"
            :renderData="[
              { textInput: false, value: '변화없음' },
              { textInput: false, value: '변화 있음' },
            ]"
            v-model="form.작업설비"
          />
        </div>
        <div class="writeGridLineItem">
          <div>작업량</div>
          <radio-text-input
            :readonly="board.readonly"
            :isPrint="isPrint"
            radioName="작업장상황조사_2"
            :renderData="[
              { textInput: false, value: '변화없음' },
              { textInput: false, value: '줄음' },
              { textInput: false, value: '늘어남' },
              { textInput: false, value: '기타' },
            ]"
            v-model="form.작업량"
          />
        </div>
        <div class="writeGridLineItem">
          <div>작업 속도</div>
          <radio-text-input
            :readonly="board.readonly"
            :isPrint="isPrint"
            radioName="작업장상황조사_3"
            :renderData="[
              { textInput: false, value: '변화없음' },
              { textInput: false, value: '줄음' },
              { textInput: false, value: '늘어남' },
              { textInput: false, value: '기타' },
            ]"
            v-model="form.작업속도"
          />
        </div>
        <div class="writeGridLineItem">
          <div>업무 변화</div>
          <radio-text-input
            :readonly="board.readonly"
            :isPrint="isPrint"
            radioName="작업장상황조사_4"
            :renderData="[
              { textInput: false, value: '변화없음' },
              { textInput: false, value: '줄음' },
              { textInput: false, value: '늘어남' },
              { textInput: false, value: '기타' },
            ]"
            v-model="form.업무변화"
          />
        </div>
      </div>
    </div>

    <!-- 작업장 상황 조사 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 작업장 상황 조사</h5>
      <!-- 1단계 : 작업별 주요 작업내용 (유해요인 조사자) -->
      <p class="text-lg py-2 mb-3"><b>1단계</b> : 작업별 주요 작업내용 (유해요인 조사자)</p>
      <div class="writeGridContainer">
        <div class="writeGridCol2">
          <div>작업내용</div>
          <span v-if="isPrint" class="print-input"> {{ form.작업내용 }} </span>
          <ml-input v-else :readonly="board.readonly" v-model="form.작업내용" />
        </div>
      </div>
      <div class="writeGridContainer">
        <div>
          <div>작업시간(근무시간)</div>
          <p v-if="isPrint" class="print-input">{{ form.근무시간시작 }} ~ {{ form.근무시간종료 }}</p>
          <div v-else class="time">
            <time-selector v-model="form.근무시간시작" :readonly="board.readonly" />
            <p>~</p>
            <time-selector v-model="form.근무시간종료" :readonly="board.readonly" />
          </div>
        </div>
        <div>
          <div>작업시간(식사시간)</div>
          <p v-if="isPrint" class="print-input">{{ form.식사시간시작 }} ~ {{ form.식사시간종료 }}</p>
          <div v-else class="time">
            <time-selector v-model="form.식사시간시작" :readonly="board.readonly" />
            <p>~</p>
            <time-selector v-model="form.식사시간종료" :readonly="board.readonly" />
          </div>
        </div>
        <div class="writeGridLineItem">
          <div>작업정보</div>
          <span v-if="isPrint" class="print-textarea"> {{ form.작업정보 }} </span>
          <ml-textarea v-else :readonly="board.readonly" v-model="form.작업정보" />
        </div>
      </div>

      <!-- 2단계 : 작업별 작업부하 및 작업빈도 (근로자 면담) -->
      <p class="text-lg py-2 mt-28px mb-3"><b>2단계</b> : 작업별 작업부하 및 작업빈도 (근로자 면담)</p>
      <table class="table1 mb-3">
        <colgroup>
          <col />
          <col width="82" />
          <col />
          <col width="82" />
        </colgroup>
        <thead>
          <th>작업 부하(A)</th>
          <th>점수</th>
          <th>작업 빈도(B)</th>
          <th>점수</th>
        </thead>
        <tbody>
          <tr>
            <td>매우 쉬움</td>
            <td>1</td>
            <td>3개월마다(년 2~3회)</td>
            <td>1</td>
          </tr>
          <tr>
            <td>쉬움</td>
            <td>2</td>
            <td>가끔(하루 또는 주 2~3일에 1회)</td>
            <td>2</td>
          </tr>
          <tr>
            <td>약간 힘듦</td>
            <td>3</td>
            <td>자주(1일 4시간)</td>
            <td>3</td>
          </tr>
          <tr>
            <td>힘듦</td>
            <td>4</td>
            <td>계속(1일 4시간 이상)</td>
            <td>4</td>
          </tr>
          <tr>
            <td>매우 힘듦</td>
            <td>5</td>
            <td>초과근무 시간(1일 8시간 이상)</td>
            <td>5</td>
          </tr>
        </tbody>
      </table>
      <table class="table2">
        <colgroup>
          <col />
          <col />
          <col width="172" />
          <col width="172" />
          <col width="172" />
        </colgroup>
        <thead>
          <th>공정 및 작업명</th>
          <th>부담작업(호)</th>
          <th>작업부하(A)</th>
          <th>작업빈도(B)</th>
          <th>총점수(AxB)</th>
        </thead>
        <tbody>
          <td>
            <span v-if="isPrint" class="print-input"> {{ form.공정및작업명 }} </span>
            <ml-input v-else :readonly="board.readonly" v-model="form.공정및작업명" class="w-full" />
          </td>
          <td>
            <span v-if="isPrint" class="print-input"> {{ form.부담작업 }} </span>
            <ml-input v-else :readonly="board.readonly" v-model="form.부담작업" class="w-full" />
          </td>
          <td>
            <span v-if="isPrint" class="print-input"> {{ form.작업부하 }} </span>
            <comma-input v-else :readonly="board.readonly" v-model="form.작업부하" class="w-full" />
          </td>
          <td>
            <span v-if="isPrint" class="print-input"> {{ form.작업빈도 }} </span>
            <comma-input v-else :readonly="board.readonly" v-model="form.작업빈도" class="w-full" />
          </td>
          <td>
            <span v-if="isPrint" class="print-input"> {{ form.작업부하 * form.작업빈도 }} </span>
            <ml-input v-else readonly :value="form.작업부하 * form.작업빈도" class="w-full" />
          </td>
        </tbody>
      </table>

      <!-- 3단계 : 유해요인평가 -->
      <p class="text-lg py-2 mt-28px mb-3"><b>3단계</b> : 유해요인평가</p>
      <photo-upload-grid-module :isShowTitle="false" :showFooterInput="false" :isCheckMode="false" :row-count="2" :grid-count="2" v-model="form.photoList" :isPrint="isPrint" :readonly="board.readonly" style="margin-top: -1.5rem" />
      <table-custom
        :board="board"
        :is-print="isPrint"
        :controls="false"
        :multiple="false"
        :tableData="[
          { id: 'input1', title: '유해요인', type: 'textarea', placeholder: '내용' },
          { id: 'input2', title: '발생원인 (문제점)', type: 'textarea', placeholder: '내용' },
          { id: 'input3', title: '비고', type: 'textarea', placeholder: '내용' },
        ]"
        v-model="form.유해요인리스트"
      />
      <table-custom
        :board="board"
        :is-print="isPrint"
        :controls="false"
        :multiple="false"
        :tableData="[
          { id: 'input1', title: '부담작업', type: 'textarea', placeholder: '내용' },
          { id: 'input2', title: '근골격계부담작업 선정 또는 미선정 사유', type: 'textarea', placeholder: '내용' },
          { id: 'input3', title: '비고', type: 'textarea', placeholder: '내용' },
        ]"
        v-model="form.부담작업리스트"
      />
    </div>

    <!--    파일첨부-->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";
import RadioTextInput from "@/components/radio/RadioTextInput";

export default {
  components: {
    PrintCheckbox,
    RadioTextInput,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      checkIds: [],
      form: {
        stdInfo: {},
        regularly: "",

        조사일시: "",
        조사자: "",
        부서명: "",
        작업공정명: "",
        작업명: "",

        작업설비: {},
        작업량: {},
        작업속도: {},
        업무변화: {},

        작업내용: "",
        근무시간시작: "",
        근무시간종료: "",
        식사시간시작: "",
        식사시간종료: "",
        작업정보: `○ 인원 :
○ 작업형태 :
○ 작업량 :
○ 작업 설비 치수
- 리프트 버튼 높이 :
○ 중량물 취급
-
** 특이사항`,

        공정및작업명: "",
        부담작업: "",
        작업부하: 0,
        작업빈도: 0,
        photoList: [],

        유해요인리스트: [{}, {}, {}],
        부담작업리스트: [{}, {}, {}],

        list: [],
        numberId: 0,

        fileList: [],
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    handleAddRow() {
      this.form.list.push({ id: ++this.form.numberId });
    },
    handleRemoveRow() {
      this.form.list = this.form.list.filter((item) => !this.checkIds.includes(item.id));
      this.checkIds = [];
    },
    onClickAllCheck() {
      if (this.checkIds.length === this.form.list.length) this.checkIds = [];
      else this.checkIds = this.form.list.map((item) => item.id);
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th,
  td {
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #cdcdcd;
    padding: 16px;
  }

  th {
    text-align: center;
    background: #f9f9f9;
  }

  &.table1 {
    td:nth-child(2n) {
      text-align: center;
    }
  }

  &.table2 {
    td {
      padding: 6px;
    }
  }
}
</style>
